"use client";

import { useEffect, useState } from "react";
import { GoogleTagManager } from "@next/third-parties/google";
import { clientLogger } from "@/lib/logger";
import { detectIOSWebview } from "@/lib/utils/tracking";

export default function ConditionalTagManager() {
	const [isTrackingAuthorized, setIsTrackingAuthorized] = useState<
		boolean | null
	>(null);
	const [isClient, setIsClient] = useState(false);

	const GA_TAG_ID = process.env.NEXT_PUBLIC_GOOGLE_TAG_ID;

	useEffect(() => {
		setIsClient(true);

		const initializeTagManager = () => {
			// More reliable iOS webview detection
			const isInIOSWebview = detectIOSWebview();

			let trackingAuthorized: boolean;

			if (isInIOSWebview) {
				// In iOS webview, respect the user's consent
				trackingAuthorized = window.appTrackingAuthorized ?? false;
			} else {
				// In web browsers, always enable tracking
				trackingAuthorized = true;
			}

			setIsTrackingAuthorized(trackingAuthorized);

			if (trackingAuthorized) {
				clientLogger.info(
					`App tracking authorized from iOS app, enabling Google Tag Manager.`,
				);
			} else {
				clientLogger.info(
					`App tracking denied from iOS app, disabling Google Tag Manager.`,
				);
			}
		};

		const handleTrackingChange = () => {
			initializeTagManager();
		};

		// Initialize immediately
		initializeTagManager();

		// Listen for tracking changes (only relevant in iOS webview)
		window.addEventListener(
			"trackingAuthorizationChanged",
			handleTrackingChange,
		);

		return () => {
			window.removeEventListener(
				"trackingAuthorizationChanged",
				handleTrackingChange,
			);
		};
	}, []);

	if (
		!isClient ||
		isTrackingAuthorized === null ||
		!isTrackingAuthorized ||
		!GA_TAG_ID
	) {
		return null;
	}

	return <GoogleTagManager gtmId={GA_TAG_ID} />;
}
